import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Upload, XCircle } from 'lucide-react';
import {
  cancelIngestionJob,
  getIngestionJobs,
  uploadIngestFile,
} from '@/api/ingestionJobs';
import type { IngestionJob } from '@/api/ingestionJobs';
import { getConnectors } from '@/api/connectors';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

const JOBS_QUERY_KEY = ['ingestion-jobs'];

const STATUS_STYLES: Record<string, string> = {
  queued: 'bg-muted text-muted-foreground',
  running: 'bg-primary/10 text-primary',
  completed: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  partial: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  failed: 'bg-destructive/10 text-destructive',
  dead_letter: 'bg-destructive/10 text-destructive',
  cancelled: 'bg-muted text-muted-foreground',
};

function JobStatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
        STATUS_STYLES[status] ?? 'bg-muted text-muted-foreground'
      }`}
    >
      {status.replace('_', ' ')}
    </span>
  );
}

function ProgressCell({ job }: { job: IngestionJob }) {
  const { total, done, failed } = job.progress;
  if (total === 0) return <span className="text-muted-foreground">-</span>;
  return (
    <span>
      {done}/{total}
      {failed > 0 && <span className="text-destructive"> ({failed} failed)</span>}
    </span>
  );
}

function UploadCard() {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [connectorId, setConnectorId] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  const { data: connectors } = useQuery({
    queryKey: ['connectors'],
    queryFn: getConnectors,
  });

  const upload = useMutation({
    mutationFn: async () => {
      const file = fileRef.current?.files?.[0];
      if (!file || !connectorId) throw new Error('Pick a connector and a file first.');
      return uploadIngestFile(file, connectorId);
    },
    onSuccess: (result) => {
      setMessage(
        `Ingested ${result.source_filename}: ${result.chunk_count} chunks ` +
          `(${result.extracted_chars.toLocaleString()} chars extracted).`,
      );
      if (fileRef.current) fileRef.current.value = '';
      queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEY });
    },
    onError: (err: Error) => setMessage(err.message),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Upload a document</CardTitle>
        <CardDescription>
          PDF, DOCX, PPTX, XLSX, CSV, images, and more. Extraction runs
          self-hosted inside your Gateco deployment.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <select
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={connectorId}
          onChange={(e) => setConnectorId(e.target.value)}
          aria-label="Target connector"
        >
          <option value="">Select a connector...</option>
          {(connectors ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <input
          ref={fileRef}
          type="file"
          className="w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-foreground"
          aria-label="Document file"
        />
        <div className="flex items-center gap-3">
          <Button
            onClick={() => upload.mutate()}
            disabled={upload.isPending}
          >
            <Upload className="mr-2 h-4 w-4" />
            {upload.isPending ? 'Ingesting...' : 'Ingest file'}
          </Button>
          {message && <p className="text-sm text-muted-foreground">{message}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export function IngestionJobsPage() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: JOBS_QUERY_KEY,
    queryFn: () => getIngestionJobs(),
    refetchInterval: 5000,
  });

  const cancel = useMutation({
    mutationFn: cancelIngestionJob,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEY }),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Ingestion"
        subtitle="Upload documents and track async ingestion jobs."
      />
      <UploadCard />
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Jobs</CardTitle>
            <CardDescription>
              Async ingestion job queue (auto-refreshes every 5s).
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEY })}
          >
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : (data?.jobs.length ?? 0) === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No ingestion jobs yet. Jobs appear here when you use the async
              jobs API or run a source-sync pipeline.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">Job</th>
                    <th className="py-2 pr-4 font-medium">Type</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    <th className="py-2 pr-4 font-medium">Progress</th>
                    <th className="py-2 pr-4 font-medium">Attempts</th>
                    <th className="py-2 pr-4 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {data?.jobs.map((job) => (
                    <tr key={job.id} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-mono text-xs">
                        {job.id.slice(0, 8)}
                      </td>
                      <td className="py-2 pr-4">{job.job_type}</td>
                      <td className="py-2 pr-4">
                        <JobStatusBadge status={job.status} />
                      </td>
                      <td className="py-2 pr-4">
                        <ProgressCell job={job} />
                      </td>
                      <td className="py-2 pr-4">
                        {job.attempts}/{job.max_attempts}
                      </td>
                      <td className="py-2 pr-4 text-right">
                        {job.status === 'queued' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => cancel.mutate(job.id)}
                          >
                            <XCircle className="mr-1 h-4 w-4" /> Cancel
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default IngestionJobsPage;
